import { CloseCircleFilled, CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { opportunityApi } from "api/opportunity.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  CodeCell,
  CustomerCell,
  StatusCell,
  TableContent,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCustomerOptions, fetchStaffOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { userStore } from "store/userStore";
import { QueryParam } from "types/query";
import {
  Opportunity,
  OpportunityCounts,
  OpportunityStatus,
  QuoteStatus,
} from "types/opportunity";
import { formatDateTime } from "utils/date";
import { can } from "utils/auth";
import { StatusTabs } from "./StatusTabs";
import {
  OPPORTUNITY_PATH,
  QUOTE_PATH,
  emptyCounts,
  formatMoney,
  opportunityStatusTrans,
  opportunityTabs,
  quoteStatusTrans,
  quoteTabs,
} from "../opportunity.meta";
import "../Opportunity.scss";

type OpportunityListProps = {
  mode: "opportunity" | "quote";
};

const countOf = (counts: OpportunityCounts, mode: OpportunityListProps["mode"], key: string) => {
  if (key === "ALL") return counts.all;
  if (mode === "quote") return counts.quoteStatuses[key as QuoteStatus] || 0;
  return counts.statuses[key as OpportunityStatus] || 0;
};

export const OpportunityList = observer(({ mode }: OpportunityListProps) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("ALL");
  const [query, setQuery] = useState<QueryParam>(() => {
    const lockedStaffId =
      mode === "opportunity" && !userStore.info.role?.isAdmin ? userStore.info.id : undefined;
    return {
      page: 1,
      limit: 50,
      search: "",
      ...(lockedStaffId ? { staffId: lockedStaffId } : {}),
    };
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Opportunity[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<OpportunityCounts>(emptyCounts());

  const path = mode === "quote" ? QUOTE_PATH : OPPORTUNITY_PATH;
  const canCreate = mode === "opportunity" && can(OPPORTUNITY_PATH, "CREATE");
  const canEdit = can(path, "UPDATE");
  const canDelete = mode === "opportunity" && can(OPPORTUNITY_PATH, "DELETE");
  const canApprove = mode === "opportunity" && can(OPPORTUNITY_PATH, "APPROVE");
  const isOpportunity = mode === "opportunity";
  const isAdmin = !!userStore.info.role?.isAdmin;
  const myStaffId = userStore.info.id;
  const lockStaffFilter = isOpportunity && !isAdmin;

  const myStaffOptions = useMemo(
    () =>
      myStaffId
        ? [{ label: userStore.info.name || userStore.info.username || "", value: myStaffId }]
        : [],
    [myStaffId, userStore.info.name, userStore.info.username],
  );

  // Bộ lọc (chỉ trang Cơ hội): khách hàng, sale phụ trách khách hàng.
  // Role không phải admin chỉ thấy chính mình và không đổi được.
  const customerFilter = useRemoteOptions(fetchCustomerOptions, isOpportunity);
  const staffFilter = useRemoteOptions(fetchStaffOptions, isOpportunity && isAdmin);

  useEffect(() => {
    if (!lockStaffFilter || !myStaffId) {
      return;
    }
    setQuery((prev) => (prev.staffId === myStaffId ? prev : { ...prev, staffId: myStaffId, page: 1 }));
  }, [lockStaffFilter, myStaffId]);

  const setQueryFilter = useCallback((key: string, value?: number) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (value) {
        next[key] = value;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const filters = useMemo(
    () =>
      isOpportunity
        ? [
          {
            key: "customerId",
            value: (query.customerId as number | undefined) ?? "",
            options: customerFilter.options,
            onChange: (value: string | number | undefined) => {
              const customerId = typeof value === "number" ? value : undefined;
              customerFilter.select(customerId);
              setQueryFilter("customerId", customerId);
            },
            placeholder: "Khách hàng",
            allowClear: true,
            showSearch: true,
            filterOption: false as const,
            onSearch: customerFilter.onSearch,
            width: 220,
          },
          {
            key: "staffId",
            value: lockStaffFilter ? (myStaffId ?? "") : ((query.staffId as number | undefined) ?? ""),
            options: lockStaffFilter ? myStaffOptions : staffFilter.options,
            onChange: (value: string | number | undefined) => {
              if (lockStaffFilter) {
                return;
              }
              const staffId = typeof value === "number" ? value : undefined;
              staffFilter.select(staffId);
              setQueryFilter("staffId", staffId);
            },
            placeholder: "Sale phụ trách",
            allowClear: !lockStaffFilter,
            showSearch: !lockStaffFilter,
            searchable: !lockStaffFilter,
            disabled: lockStaffFilter,
            filterOption: false as const,
            onSearch: lockStaffFilter ? undefined : staffFilter.onSearch,
            width: 200,
          },
        ]
        : [],
    [
      customerFilter,
      isOpportunity,
      lockStaffFilter,
      myStaffId,
      myStaffOptions,
      query.customerId,
      query.staffId,
      setQueryFilter,
      staffFilter,
    ],
  );

  const fetchData = useCallback(async (queryParams: QueryParam, tabKey: string, listMode: OpportunityListProps["mode"]) => {
    setLoading(true);
    try {
      const params: QueryParam = {
        ...queryParams,
        countScope: listMode === "quote" ? "quote" : "opportunity",
      };
      if (listMode === "quote") {
        params.hasQuote = "true";
        if (tabKey !== "ALL") params.quoteStatus = tabKey;
      } else {
        params.hasQuote = "false";
        if (tabKey !== "ALL") params.status = tabKey;
      }

      const res = await opportunityApi.findAll(params);
      setData(res.data.opportunitys || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || emptyCounts());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query, tab, mode);
  }, [fetchData, mode, query, tab]);

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handleTabChange = useCallback((key: string) => {
    setTab(key);
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const openDetail = useCallback(
    (opportunity: Opportunity) => {
      const detailPath = mode === "quote" ? QUOTE_PATH : OPPORTUNITY_PATH;
      navigate(`${detailPath}/${opportunity.id}`);
    },
    [mode, navigate],
  );

  const openCreate = useCallback(() => {
    navigate(`${OPPORTUNITY_PATH}/create`, { state: { from: "opportunity" } });
  }, [navigate]);

  const stopRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleApprove = useCallback(
    async (opportunity: Opportunity) => {
      setLoading(true);
      try {
        await opportunityApi.approve(opportunity.id);
        message.success("Đã duyệt báo giá");
        await fetchData(query, tab, mode);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, mode, query, tab],
  );

  const handleDelete = useCallback(
    async (opportunity: Opportunity) => {
      setLoading(true);
      try {
        await opportunityApi.delete(opportunity.id);
        message.success("Xóa cơ hội thành công");
        await fetchData(query, tab, mode);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, mode, query, tab],
  );

  const tabs = (mode === "quote" ? quoteTabs : opportunityTabs).map((item) => ({
    ...item,
    count: countOf(counts, mode, item.key),
  }));

  const columns = useMemo<TableColumnsType<Opportunity>>(
    () => [
      {
        title: "Mã báo giá",
        dataIndex: "code",
        key: "code",
        width: 130,
        render: (value: string) => (value ? <CodeCell value={value} /> : <TableContent>--</TableContent>),
      },
      {
        title: "Tên cơ hội",
        dataIndex: "name",
        key: "name",
        render: (value: string) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Khách hàng",
        key: "customer",
        width: 220,
        render: (_, record) => <CustomerCell customer={record.customer} />,
      },
      // Trang Cơ hội chỉ hiện trạng thái cơ hội; trang Báo giá chỉ hiện trạng thái báo giá
      isOpportunity
        ? {
          title: "Trạng thái cơ hội",
          dataIndex: "status",
          key: "status",
          width: 170,
          render: (value: OpportunityStatus) => <StatusCell value={value} trans={opportunityStatusTrans} />,
        }
        : {
          title: "Trạng thái báo giá",
          dataIndex: "quoteStatus",
          key: "quoteStatus",
          width: 170,
          render: (value: QuoteStatus | null) =>
            value ? <StatusCell value={value} trans={quoteStatusTrans} /> : <TableContent>--</TableContent>,
        },
      {
        title: "Tổng tiền",
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: 140,
        align: "right",
        render: (value: number) => <TableContent>{formatMoney(value || 0)}</TableContent>,
      },
      {
        title: "Ngày tạo",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 170,
        render: (value: number) => <TableContent>{formatDateTime(value)}</TableContent>,
      },
      ...(isOpportunity
        ? [
          {
            title: "Người tạo",
            key: "createdStaff",
            width: 150,
            render: (_: unknown, record: Opportunity) => (
              <TableContent>{record.createdStaff?.name || record.createdStaff?.username || "--"}</TableContent>
            ),
          },
        ]
        : []),
      ...(canEdit || canDelete || canApprove
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 150,
            align: "center" as const,
            render: (_: unknown, record: Opportunity) => (
              <div className="flex gap-2 justify-center items-center" onClick={stopRowClick}>
                {canApprove && record.status === OpportunityStatus.WaitApprove && !record.quoteStatus && (
                  <Tooltip title="Duyệt báo giá">
                    <Popconfirm
                      classNames={{ root: "opportunityApproveConfirm" }}
                      cancelText="Không"
                      okText="Duyệt"
                      onConfirm={() => handleApprove(record)}
                      title="Xác nhận duyệt báo?"
                    >
                      <ActionButton type="primary" aria-label="Duyệt báo giá">
                        <CheckOutlined />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
                {canEdit && (
                  <Tooltip title="Cập nhật">
                    <ActionButton aria-label="Cập nhật" onClick={() => openDetail(record)}>
                      <EditOutlined />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xác nhận xóa?"
                    >
                      <ActionButton danger aria-label="Xóa">
                        <DeleteOutlined />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canApprove, canDelete, canEdit, handleApprove, handleDelete, isOpportunity, openDetail, stopRowClick],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div className="opportunityPage">
      <StatusTabs items={tabs} activeKey={tab} onChange={handleTabChange} />
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 420,
              placeholder: "Tìm theo tên, mã, khách hàng",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              canCreate && (
                <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Tạo mới
                </AppButton>
              )
            }
            filterLabel={null}
            filters={filters}
          />
        }
        table={
          <AppTable<Opportunity>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            onRow={(record) => ({
              onClick: () => openDetail(record),
            })}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />
    </div>
  );
});
